// ─────────────────────────────────────────────────────────────
// fusion-jit.ts — JIT WGSL generator for fused single-qubit
// chains of specific length k.
//
// A runtime-bounded loop `for g in 0..params.k` doesn't get fully
// unrolled by the WGSL/MSL/SPIR-V toolchain, so each iteration pays
// a branch + a uniform read per gate on top of the register matmul.
// That shows up in E9 as a non-amortizing residual C in
//   α_eff(k) = α_raw / k + C.
//
// This module emits a per-k shader with the gate loop fully unrolled
// to k explicit blocks (no `for`, no conditionals). The compiler now
// sees the uniform accesses at constant offsets and schedules them
// as plain memory loads — same code path as a single-gate shader,
// just k times. A tiny in-process cache avoids recompiling for the
// same k twice; cold-compile times are sub-millisecond per k.
//
// Uniform-buffer layout: 16 B header (qubit, n_qubits, k, stride_inv)
// followed by MAX_K × 32 B gate slots. All per-k pipelines share one
// buffer on the owning QuantumCircuit — only the shader changes.
// ─────────────────────────────────────────────────────────────

import { FUSED_CHAIN_MAX_K } from "./fusion-max-k.js";

/** Emit WGSL source for a fused chain of exactly `k` single-qubit gates.
 *
 * Uniform buffer layout: 16 B header + MAX_K × 32 B body — every per-k
 * pipeline shares the same buffer, only the shader differs.
 *
 * `k` must be in [1, FUSED_CHAIN_MAX_K].
 */
export function emitFusedChainWgsl(k: number): string {
  if (!Number.isInteger(k) || k < 1 || k > FUSED_CHAIN_MAX_K) {
    throw new Error(`emitFusedChainWgsl: k=${k} out of [1, ${FUSED_CHAIN_MAX_K}]`);
  }

  // Emit k explicit {…} blocks that each read one Gate and update
  // the register pair (a, b). Using blocks (rather than a flat loop)
  // keeps `na`, `nb` as fresh names per iteration and mirrors the
  // static shader's body structure.
  const unrolled: string[] = [];
  for (let g = 0; g < k; g++) {
    unrolled.push(
      `  {
    let gate${g} = params.gates[${g}u];
    let na = cmul(gate${g}.m00, a) + cmul(gate${g}.m01, b);
    let nb = cmul(gate${g}.m10, a) + cmul(gate${g}.m11, b);
    a = na;
    b = nb;
  }`,
    );
  }
  const body = unrolled.join("\n");

  return `// JIT-emitted fused chain for k=${k}.
// Auto-generated by emitFusedChainWgsl(${k}); do not hand-edit.

const MAX_K: u32 = ${FUSED_CHAIN_MAX_K}u;
const CHAIN_K: u32 = ${k}u;

struct Gate {
  m00: vec2<f32>,
  m01: vec2<f32>,
  m10: vec2<f32>,
  m11: vec2<f32>,
};

struct Params {
  qubit: u32,
  n_qubits: u32,
  k: u32,
  stride_inv: u32,
  gates: array<Gate, MAX_K>,
};

@group(0) @binding(0) var<storage, read_write> psi: array<vec2<f32>>;
@group(0) @binding(1) var<uniform> params: Params;

fn cmul(a: vec2<f32>, b: vec2<f32>) -> vec2<f32> {
  return vec2<f32>(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x);
}

@compute @workgroup_size(256)
fn apply(@builtin(global_invocation_id) gid: vec3<u32>) {
  let pair_idx = gid.y * params.stride_inv + gid.x;
  let n_pairs = 1u << (params.n_qubits - 1u);
  if (pair_idx >= n_pairs) { return; }

  let q = params.qubit;
  let mask_low = (1u << q) - 1u;
  let i = ((pair_idx & ~mask_low) << 1u) | (pair_idx & mask_low);
  let j = i | (1u << q);

  var a = psi[i];
  var b = psi[j];

${body}

  psi[i] = a;
  psi[j] = b;
}
`;
}

/** Per-device cache of JIT-compiled fused-chain pipelines, keyed by chain length k. */
export class FusedPipelineCache {
  private readonly pipelines = new Map<number, GPUComputePipeline>();
  private readonly device: GPUDevice;
  /** Cold-compile wall time (ms) for each k we've ever compiled — useful for E9's
   *  "cold-start α" diagnostic. Map is keyed by k, value is ms for that compile. */
  readonly coldCompileMs = new Map<number, number>();

  constructor(device: GPUDevice) {
    this.device = device;
  }

  /** Get (or lazily create) a compute pipeline for a fused chain of length k. */
  get(k: number): GPUComputePipeline {
    const cached = this.pipelines.get(k);
    if (cached) return cached;

    const t0 = performance.now();
    const code = emitFusedChainWgsl(k);
    const mod = this.device.createShaderModule({
      label: `fused-chain-jit-k${k}`,
      code,
    });
    // Surface WGSL errors loudly — silent compile failures turn into no-op
    // dispatches, which look correct but produce wrong statevectors.
    mod.getCompilationInfo().then((info) => {
      for (const m of info.messages) {
        if (m.type === "error") {
          console.error(`[webgpu-q] fused-chain-jit-k${k}:${m.lineNum}:${m.linePos} ${m.message}`);
        } else if (m.type === "warning") {
          console.warn(`[webgpu-q] fused-chain-jit-k${k}:${m.lineNum}:${m.linePos} ${m.message}`);
        }
      }
    });
    const pipeline = this.device.createComputePipeline({
      label: `fused-chain-jit-pipeline-k${k}`,
      layout: "auto",
      compute: { module: mod, entryPoint: "apply" },
    });
    this.coldCompileMs.set(k, performance.now() - t0);
    this.pipelines.set(k, pipeline);
    return pipeline;
  }

  /** Pre-warm the cache for a set of ks (useful for E9 to exclude cold-start from trial timing). */
  warm(ks: Iterable<number>): void {
    for (const k of ks) this.get(k);
  }

  /** Size of cache (for introspection). */
  size(): number {
    return this.pipelines.size;
  }
}
